/*
 * Copyright 2022-2026 NXGN Management, LLC. All Rights Reserved.
 */

import { Component, Input, OnInit } from "@angular/core";
import { FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import { ApiCustomer, BusinessTypes, Merchant, MerchantMigrationDetails, ownershipTypes } from "../../../../models";
import { MERCHANT_CONSTANTS, MERCHANT_MIGRATION_STATUS } from "../../../../utils/data.rest.constants";
import { AuthService, MerchantService } from "../../../../services";
import * as moment from "moment";
import { NotificationService } from ".././../../../services/notification.service";
import { catchError, debounceTime, distinctUntilChanged, finalize, map, switchMap, tap } from "rxjs/operators";
import { client } from "../../../../models/client";
import { Location } from "../../../../models";
import { of } from "rxjs";

@Component({
  selector: "app-general-info-edit",
  templateUrl: "./general-info-edit.component.html",
  styleUrls: ["./general-info-edit.component.scss"]
})
export class GeneralInfoEditComponent implements OnInit {
  public ownershipTypes = ownershipTypes;
  public businessTypes = BusinessTypes;
  public sicMccCodes = MERCHANT_CONSTANTS.SIC_MCC_CODE;
  public payApiCustomers!: ApiCustomer[];
  @Input() parentGroup!: FormGroup;
  @Input() generalInfo!: FormGroup;
  @Input("merchant") merchant!: Merchant;
  @Input("isEditable") isEditable!: boolean;
  @Input("hiddenField") hiddenField!: boolean;
  maxDate = new Date();
  minDate = new Date();
  public migrationInfo!: string;
  isMigrationDetailsLoading = false;
  cancellationInProgress = false;
  displayMigrationDetails = false;
  displayMigrationVerbiage = false;
  isMerchantMigrated = false;
  isInstamedMigrationMerchant = false;
  isPracticeIDValid = true;
  hasPracticeId = true;
  loadingClients = false;
  loadingLocations = false;
  errorLoadingLocations = false;
  hasSearchedLocations = false;
  hasSearchedClients = false;
  public filteredClients: client[] = [];
  public filteredLocations: Location[] = [];

  onPracticeNameInput() {
    this.generalInfo.patchValue({
      siteId: "",
      enterpriseId: "",
      enterprisePracticeId: "",
      practiceId: "",
      practiceName: "",
      clientName: "",
      gcn: ""
    });

    this.generalInfo.patchValue({ locationName: "", locationId: "", locationAlias: "" }, { emitEvent: false });
    this.generalInfoFormControl.locationNameSearch.setValue("", { emitEvent: false });
    this.generalInfoFormControl.locationAlias.setValue("", { emitEvent: false });
    this.generalInfoFormControl.locationNameSearch.disable();
    this.generalInfoFormControl.locationAlias.disable();

    this.filteredClients = [];
    this.filteredLocations = [];
    this.hasSearchedLocations = false;
    this.loadingLocations = false;
  }

  onLocationInput() {
    this.generalInfo.patchValue({
      locationName: "",
      locationId: "",
      locationAlias: ""
    });
    this.generalInfoFormControl.locationAlias.disable();
    this.filteredLocations = [];
  }

  selectClient(client: any) {
    this.generalInfo.patchValue(
      {
        practiceNameSearch: client.practiceName,
        clientName: client.clientName,
        siteId: client.siteId,
        enterpriseId: client.enterpriseId,
        enterprisePracticeId: client.enterprisePracticeId,
        practiceId: client.practiceId,
        practiceName: client.practiceName,
        gcn: client.gcn
      },
      { emitEvent: false }
    );

    this.generalInfo.patchValue({ locationName: "", locationId: "", locationAlias: "" }, { emitEvent: false });
    this.generalInfoFormControl.locationNameSearch.setValue("", { emitEvent: false });
    this.generalInfoFormControl.locationAlias.setValue("", { emitEvent: false });
    this.generalInfoFormControl.locationNameSearch.enable();

    this.filteredClients = [];
    this.filteredLocations = [];
  }

  selectLocation(location: Location) {
    if (!location) return;

    this.generalInfo.patchValue(
      {
        locationName: location.locationName || "",
        locationId: location.locationId || "",
        locationAlias: location.locationAlias || ""
      },
      { emitEvent: false }
    );

    this.generalInfoFormControl.locationNameSearch.setValue(location.locationName || "", { emitEvent: false });
    this.generalInfoFormControl.locationAlias.setValue((location.locationName || "").slice(0, 40), {
      emitEvent: false
    });
    this.generalInfoFormControl.locationAlias.enable();
    this.filteredLocations = [];
  }

  constructor(
    private merchantService: MerchantService,
    public authService: AuthService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.generalInfo?.addControl("tokenConversionDate", new FormControl(""));
    this.maxDate = moment().add(6, "months").toDate();
    if (!this.merchantService.apiCustomers) {
      this.merchantService.getPayApiCustomers().subscribe((customers) => (this.payApiCustomers = customers));
    } else {
      this.payApiCustomers = this.merchantService.apiCustomers;
    }
    if (this.isInstaMed() && !this.hasFinanceRole()) {
      this.generalInfoFormControl.merchantName.disable();
      this.generalInfoFormControl.merchantName.updateValueAndValidity();
    } else {
      this.generalInfoFormControl.merchantName.enable();
      this.generalInfoFormControl.merchantName.updateValueAndValidity();
    }

    if (this.merchant?.merchantMigrationDetails?.toBeMigrated && this.isEditable) {
      this.generalInfoFormControl?.instaMedConversionStatus?.patchValue(true);
      this.generalInfoFormControl?.previousMMID?.patchValue(this.merchant.merchantMigrationDetails?.previousMmid);
      this.generalInfoFormControl?.tokenConversionDate?.patchValue(
        this.merchant.merchantMigrationDetails?.effectiveDate
      );
      this.generalInfoFormControl?.previousMMID?.disable();
      this.generalInfoFormControl?.externalMerchantId.addValidators(Validators.required);
    } else {
      this.generalInfoFormControl?.instaMedConversionStatus?.patchValue(false);
      this.generalInfoFormControl?.previousMMID?.patchValue("");
      this.generalInfoFormControl.previousMMID?.removeValidators(Validators.required);
      this.generalInfoFormControl?.tokenConversionDate?.patchValue("");
      this.generalInfoFormControl?.previousMMID?.enable();
      this.generalInfoFormControl?.externalMerchantId.removeValidators(Validators.required);
    }

    if (this.authService.hasProvisionRole() || this.merchant.merchantMigrationDetails?.toBeMigrated) {
      this.generalInfoFormControl?.instaMedConversionStatus?.disable();
    } else {
      this.generalInfoFormControl?.instaMedConversionStatus?.enable();
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.isInstamedMigrationMerchant = this.merchant?.id === 0 ? true : this.merchant.migratedToInstaMed!;
    this.isMerchantMigrated =
      this.merchant.merchantMigrationDetails?.migrationStatus === MERCHANT_MIGRATION_STATUS.COMPLETE ||
      this.merchant.merchantMigrationDetails?.migrationStatus === MERCHANT_MIGRATION_STATUS.IN_PROGRESS;
    this.disablePracticeIdForMigratedMerchant();

    this.generalInfo.addControl("practiceNameSearch", new FormControl(""));
    this.generalInfo.addControl("siteId", new FormControl({ value: this.merchant.siteId, disabled: true }));
    this.generalInfo.addControl("enterpriseId", new FormControl({ value: this.merchant.enterpriseId, disabled: true }));
    this.generalInfo.addControl(
      "enterprisePracticeId",
      new FormControl({ value: this.merchant.practiceId, disabled: true })
    );
    this.generalInfo.addControl("practiceId", new FormControl({ value: this.merchant.practiceId, disabled: true }));
    this.generalInfo.addControl("clientName", new FormControl({ value: this.merchant.clientName, disabled: true }));
    this.generalInfo.addControl("practiceName", new FormControl({ value: this.merchant.practiceName, disabled: true }));
    this.generalInfo.addControl("gcn", new FormControl({ value: this.merchant.gcn, disabled: true }));
    this.generalInfo.addControl("locationNameSearch", new FormControl(""));
    this.generalInfoFormControl.locationNameSearch.setValue(this.merchant?.locationName || "", { emitEvent: false });
    this.generalInfo.addControl("locationName", new FormControl({ value: this.merchant.locationName, disabled: true }));
    this.generalInfo.addControl("locationId", new FormControl({ value: this.merchant.locationId, disabled: true }));
    this.generalInfo.addControl(
      "locationAlias",
      new FormControl({ value: this.merchant.locationAlias, disabled: false })
    );

    this.isLocationDropdownDisabled()
      ? this.generalInfoFormControl.locationNameSearch.disable()
      : this.generalInfoFormControl.locationNameSearch.enable();
    this.merchant?.locationName
      ? this.generalInfoFormControl.locationAlias.enable()
      : this.generalInfoFormControl.locationAlias.disable();

    this.generalInfoFormControl.practiceNameSearch.valueChanges
      .pipe(
        map((raw: string) => (raw || "").trim()),
        tap(() => {
          this.hasSearchedClients = false;
          this.filteredClients = [];
        }),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term: string) => {
          if (!term || term.length < 3) {
            this.loadingClients = false;
            this.hasSearchedClients = false;
            return of({ term, results: [] as client[] });
          }

          this.loadingClients = true;

          return this.merchantService.fetchClientMerchant(term).pipe(
            map((clients: client[]) => ({ term, results: clients || [] })),
            catchError((err) => {
              console.error("Error fetching clients", err);
              return of({ term, results: [] as client[] });
            }),
            finalize(() => {
              this.loadingClients = false;
            })
          );
        })
      )
      .subscribe(
        ({ term, results }) => {
          setTimeout(() => {
            this.hasSearchedClients = true;
          }, 25);

          const current = (this.generalInfoFormControl.practiceNameSearch.value || "").trim();
          if (current === term && term.length >= 3) {
            this.filteredClients = results;
          } else {
            this.filteredClients = [];
          }
          this.loadingClients = false;
        },
        () => {
          this.filteredClients = [];
          this.loadingClients = false;
          this.hasSearchedClients = true;
        }
      );

    this.generalInfoFormControl.locationNameSearch.valueChanges
      .pipe(
        map((raw: string) => (raw || "").trim()),
        tap((term: string) => {
          this.hasSearchedLocations = false;
          this.filteredLocations = [];

          if (term && term.length >= 3) {
            this.loadingLocations = true;
          } else {
            this.loadingLocations = false;
          }
        }),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term: string) => {
          if (!term || term.length < 3) {
            this.loadingLocations = false;
            this.hasSearchedLocations = false;
            return of({ term, results: [] as Location[] });
          }

          const siteId = this.generalInfoFormControl.siteId?.value;
          const enterpriseId = this.generalInfoFormControl.enterpriseId?.value;
          const practiceId = this.generalInfoFormControl.enterprisePracticeId?.value;

          if (!siteId || !enterpriseId || !practiceId) {
            this.loadingLocations = false;
            this.hasSearchedLocations = false;
            return of({ term, results: [] as Location[] });
          }

          return this.merchantService.fetchClientLocations(siteId, enterpriseId, practiceId).pipe(
            map((locations: Location[]) => {
              this.errorLoadingLocations = false;
              const lower = term.toLowerCase();
              const filtered = (locations || [])
                .filter((l) => {
                  const name = (l.locationName || "").toLowerCase();
                  const alias = (l.locationAlias || "").toLowerCase();
                  const id = (l.locationId || "").toLowerCase();
                  return name.includes(lower) || alias.includes(lower) || id.includes(lower);
                })
                .slice(0, 25);
              return { term, results: filtered };
            }),
            catchError(() => {
              this.errorLoadingLocations = true;
              return of({ term, results: [] as Location[] });
            }),
            finalize(() => {
              this.loadingLocations = false;
            })
          );
        })
      )
      .subscribe(
        ({ term, results }) => {
          setTimeout(() => {
            this.hasSearchedLocations = true;
          }, 25);

          const current = (this.generalInfoFormControl.locationNameSearch.value || "").trim();
          if (current === term && term.length >= 3) {
            this.filteredLocations = results;
          } else {
            this.filteredLocations = [];
          }
        },
        () => {
          this.filteredLocations = [];
          this.loadingLocations = false;
          this.hasSearchedLocations = true;
        }
      );

    // initialize filter arrays
    this.filteredClients = [];
    this.filteredLocations = [];
  }

  disablePracticeIdForMigratedMerchant(): void {
    if (
      this.merchant.migratedToInstaMed &&
      this.merchant.merchantMigrationDetails?.instaMedMmid &&
      !this.merchantService.isInstaMed(this.merchant)
    ) {
      this.generalInfoFormControl?.externalMerchantId.disable();
    } else {
      this.generalInfoFormControl?.externalMerchantId.enable();
    }
  }

  get generalInfoFormControl() {
    return this.generalInfo.controls;
  }

  isCancelButtonEnabled(): boolean {
    return this.merchantMigrationDetails().previousMmid !== 0;
  }

  fetchPreviousMmidInfo() {
    this.generalInfo.controls["previousMMID"].markAsTouched();
    if (this.generalInfo.controls["previousMMID"].valid) {
      this.generalInfo.controls["previousMMID"].disable();
      this.isMigrationDetailsLoading = true;
      this.merchantService.getPreviousMerchantDetails(Number(this.generalInfoFormControl.previousMMID.value)).subscribe(
        (merchantDetails: MerchantMigrationDetails) => {
          this.merchantService.merchantMigrationDetails = merchantDetails;
          this.merchant.merchantMigrationDetails = merchantDetails;
          this.hasPracticeId = !!merchantDetails.practiceId;
          this.isPracticeIDValid = this.merchantService.isPracticeIdMatched(
            this.generalInfoFormControl.externalMerchantId.value
          );
          this.displayMigrationDetails = !merchantDetails.toBeMigrated;
          this.displayMigrationVerbiage = !!merchantDetails.toBeMigrated;
          if (this.merchantService.merchantMigrationDetails.toBeMigrated) {
            this.migrationInfo = this.merchantService.generateMigrationInfoVerbiage(merchantDetails);
          }
          this.generalInfo.controls["previousMMID"].enable();
          this.isMigrationDetailsLoading = false;
        },
        (error) => {
          this.isMigrationDetailsLoading = false;
          this.generalInfo.controls["previousMMID"].enable();
          this.notificationService.error(error.error);
        }
      );
    }
  }

  merchantMigrationDetails(): MerchantMigrationDetails {
    return this.merchantService.merchantMigrationDetails;
  }

  handlePracticeId(practiceId: number): void {
    this.isPracticeIDValid = this.merchantService.isPracticeIdMatched(practiceId);
  }

  handlePreviousMmidLinkage(): void {
    this.cancellationInProgress = true;
    this.merchantService.cancelPreviousMmidLinkage(this.generalInfoFormControl.previousMMID.value).subscribe(
      () => {
        this.generalInfoFormControl.previousMMID.enable();
        this.generalInfoFormControl?.instaMedConversionStatus?.enable();
        this.generalInfo.controls["previousMMID"]?.addValidators(Validators.required);
        this.generalInfo.controls["previousMMID"]?.markAsTouched();
        this.cancellationInProgress = false;
        this.generalInfoFormControl.previousMMID.reset();
      },
      (error) => {
        this.cancellationInProgress = false;
        this.notificationService.error(error.error);
      }
    );
  }

  resetMerchantMigrationDetails(): void {
    this.merchantService.merchantMigrationDetails = {
      effectiveDate: "",
      instaMedMmid: 0,
      migratedMerchantName: "",
      migrationStatus: "",
      previousMerchantName: "",
      previousMmid: 0,
      previousProcessor: "",
      toBeMigrated: true,
      practiceId: 0
    };

    this.displayMigrationDetails = false;
    this.displayMigrationVerbiage = false;
    this.isPracticeIDValid = true;
    this.hasPracticeId = true;
  }

  get beneficialOwnersFormArray(): FormArray {
    return this.parentGroup.controls["principals"] as FormArray;
  }

  hasFinanceRole(): boolean {
    return this.authService.canUserAccess("FINANCE");
  }

  isWorldPay(): boolean {
    return this.merchantService.isWorldPay(this.merchant);
  }

  isInstaMed(): boolean {
    return this.merchantService.isInstaMed(this.merchant);
  }

  isGlobalPayments(): boolean {
    return this.merchantService.isGlobalPayments(this.merchant);
  }

  byId(item1: ApiCustomer, item2: ApiCustomer) {
    return item1.id === item2.id;
  }

  resetContactTypes() {
    this.merchant.accountDetails.worldPayAccountDetails.ownershipType = this.generalInfoFormControl.ownershipType.value;
    if (this.generalInfoFormControl.ownershipType.value === "SoleProprietorship") {
      this.beneficialOwnersFormArray.controls[0].get("ownerType")?.setValue("BeneficialOwner 1");
    } else {
      this.beneficialOwnersFormArray.controls[0].get("ownerType")?.setValue("Control Owner");
    }
  }

  isLocationDropdownDisabled(): boolean {
    return (
      !this.generalInfoFormControl.siteId.value ||
      !this.generalInfoFormControl.enterprisePracticeId.value ||
      !this.generalInfoFormControl.enterpriseId.value
    );
  }

  get locationPlaceholderText(): string {
    return this.isLocationDropdownDisabled() ? "" : "Start typing location name...";
  }
}
